import React from 'react';

import { Icon } from '@/components/icon';
import { STRIPED_STYLE } from '@/components/modules/route/bar-mini';
import { RouteTooltip } from '@/components/modules/route/tooltip';
import { Tooltip } from '@/components/tooltip';
import { TrafficLight } from '@/components/traffic-light';
import { MODE_TRANSFER } from '@/lib/constants';
import { SegmentModeType } from '@/types/api-types';
import { convertModeType } from '@/utils/format';
import { tv } from '@/utils/styles';

export interface JourneySequenceBarProps {
  title: string;
  description: string;
  durationSeconds: number;
  mode: SegmentModeType;
  percentage: number;
  hasDelay?: boolean;
  isSequenceEnd?: boolean;
  isSequenceStart?: boolean;
  isShortTransfer?: boolean;
  isStop?: boolean;
  hasEvents?: boolean;
  isTooltipOpen?: boolean;
  shouldShowIcon?: boolean;
  shouldShowStopIndicator?: boolean;
}

const JourneySequenceBar = ({
  title,
  description,
  durationSeconds,
  mode,
  percentage,
  isTooltipOpen,
  hasDelay = false,
  isSequenceEnd = false,
  isSequenceStart = false,
  isShortTransfer = false,
  isStop = false,
  hasEvents = false,
  shouldShowIcon = false,
  shouldShowStopIndicator = false,
}: JourneySequenceBarProps) => {
  const isTransfer = mode === MODE_TRANSFER;
  const { segmentWrapper, stopIndicator, icon, delayBadge, transferBar } = styles({
    mode,
  });

  return (
    <Tooltip
      followCursor="horizontal"
      isOpen={isTooltipOpen}
      content={
        <RouteTooltip
          title={title}
          description={description}
          durationSeconds={durationSeconds}
          isShortTransfer={isShortTransfer}
        />
      }
    >
      <div
        className={segmentWrapper({
          hasStopMarker: isStop || hasEvents,
          hasEvents,
          isSequenceEnd,
          isSequenceStart,
          isTransfer,
        })}
        data-testid="segment-bar"
        style={{ width: `${percentage}%` }}
      >
        {Boolean(isTransfer) && <span className={transferBar()} style={STRIPED_STYLE} />}
        {hasDelay && shouldShowIcon && (
          <div className={delayBadge()}>
            <TrafficLight color="red" showAlertIcon />
          </div>
        )}
        {Boolean(shouldShowIcon) && <Icon name={convertModeType(mode)} className={icon()} />}
        {Boolean(shouldShowStopIndicator) && <span className={stopIndicator()} />}
      </div>
    </Tooltip>
  );
};

const styles = tv({
  slots: {
    icon: 'absolute left-0 top-0 grid h-5 w-5 place-items-center rounded-2xl [&_svg]:h-[14px]',
    segmentWrapper: 'relative z-2 h-5 min-w-5 first:rounded-l-2xl last:rounded-r-2xl [&_svg]:fill-white',
    altBadge:
      'absolute -right-1 -top-1.5 z-5 h-3 min-w-3 items-center justify-center rounded bg-yellow px-0.5 py-0 text-center text-xxs leading-[10px] text-text-primary',
    segmentLabel: 'absolute -bottom-5 left-1 whitespace-nowrap bg-white',
    delayBadge: 'absolute -top-0.5 left-3.5 z-5',
    stopIndicator: 'absolute left-1/2 top-1/2 -ml-[3px] -mt-[3px] block h-[6px] w-3 rounded bg-white opacity-70',
    transferBar: 'absolute -left-2.5 -right-2.5 bottom-0 top-0 rounded-none !bg-grey-300 bg-[length:4px] bg-repeat',
  },
  variants: {
    mode: {
      air: {
        // @TODO: hardcoded colours to be changed to tokens once tokens are established
        segmentWrapper: 'bg-[#BFDB9C] hover:bg-transport-plane',
        icon: 'bg-transport-plane',
      },
      water: {
        segmentWrapper: 'bg-[#84D4EC] hover:bg-transport-ship',
        icon: 'bg-transport-ship',
      },
      rail: {
        segmentWrapper: 'bg-[#F9C89A] hover:bg-transport-rail',
        icon: 'bg-transport-rail',
      },
      road: {
        segmentWrapper: 'bg-[#e2e2e2] hover:bg-transport-road',
        icon: 'bg-transport-road',
      },
      transfer: {},
    },
    isSequenceStart: {
      true: {
        segmentWrapper: 'rounded-l-2xl',
      },
    },
    isSequenceEnd: {
      true: {
        segmentWrapper: 'rounded-r-2xl',
      },
    },
    hasStopMarker: {
      true: {
        segmentWrapper:
          "min-w-4 after:absolute after:left-1 after:right-1 after:top-1/2 after:-mt-[3px] after:h-1.5 after:rounded after:bg-white after:opacity-70 after:content-[''] hover:after:opacity-100",
      },
    },
    hasEvents: {
      true: {
        segmentWrapper: 'after:left-1/2 after:max-w-4 after:-translate-x-1/2',
      },
    },
    isTransfer: {
      true: {
        segmentWrapper: 'z-1',
      },
    },
  },
});

export { JourneySequenceBar };
